// src/components/Modals/CreateBatchModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { usePlanner } from "../../contexts/PlannerContext";
import { useFeedback } from "../../contexts/FeedbackContext";
// Prefer your shared builder to keep events consistent across the app
import { buildBatchEvents } from "../../utils/scheduleBatchTasks";

export default function CreateBatchModal({ onClose, products = [], batchToEdit }) {
  const { upsertEvents } = usePlanner();
  const { play, buzz } = useFeedback?.() || { play: () => {}, buzz: () => {} };

  // Form state
  const [batchName, setBatchName] = useState(batchToEdit?.name || "");
  const [selectedIds, setSelectedIds] = useState(batchToEdit?.products || []);
  const [startDate, setStartDate] = useState(
    batchToEdit?.startDate || new Date().toISOString().slice(0, 10)
  );
  // Normalize legacy "Planned" to "Not Started"
  const [status, setStatus] = useState(
    batchToEdit?.status === "Planned" ? "Not Started" : (batchToEdit?.status || "Not Started")
  );
  const [autoGenerateTasks, setAutoGenerateTasks] = useState(!batchToEdit);
  const [err, setErr] = useState("");

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); play("tap"); buzz("light"); onClose?.(false); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") { e.preventDefault(); handleSave(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [batchName, selectedIds, startDate, status, autoGenerateTasks]);

  const productIndex = useMemo(() => {
    const map = new Map();
    for (const p of products) map.set(String(p.id), p);
    return map;
  }, [products]);

  const totalCost = useMemo(() => {
    const sum = (selectedIds || []).reduce((acc, id) => {
      const prod = productIndex.get(String(id));
      return acc + Number(prod?.finalPrice || 0);
    }, 0);
    return Number.isFinite(sum) ? sum : 0;
  }, [selectedIds, productIndex]);

  function toggleProduct(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  }

  function validate() {
    if (!batchName.trim()) return "Please enter a batch name.";
    if (!startDate) return "Please select a start date.";
    if ((selectedIds || []).length === 0) return "Please select at least one product.";
    return "";
  }

  function handleSave() {
    const v = validate();
    if (v) {
      setErr(v);
      play("error"); buzz("error");
      return;
    }

    const id = batchToEdit?.id || Date.now().toString();
    const newBatch = {
      id,
      name: batchName.trim(),
      products: selectedIds,
      createdDate: batchToEdit?.createdDate || new Date().toISOString().split("T")[0],
      startDate,
      status, // already normalized
      totalCost: Number(totalCost.toFixed(2)),
    };

    // Persist to local storage
    try {
      const key = "mudoBatches";
      const saved = JSON.parse(localStorage.getItem(key) || "[]");
      const idx = saved.findIndex((b) => b.id === newBatch.id);
      if (idx >= 0) saved[idx] = newBatch; else saved.push(newBatch);
      localStorage.setItem(key, JSON.stringify(saved));
    } catch {}

    // Auto-generate planner tasks (using app-wide builder if present)
    if (!batchToEdit && autoGenerateTasks) {
      try {
        const enriched = selectedIds.map((pid) => productIndex.get(String(pid))).filter(Boolean);
        const representative = enriched[0] || {};
        const payload = {
          batchName: newBatch.name,
          product: representative,          // util expects a product; we’ll still tag each by productId in the builder
          qty: 1,                           // per-product qty can be baked into build util or left as 1
          startDate: newBatch.startDate,
          assigneeId: undefined,
          category: "Production",
          color: "#f97316",
          productIds: selectedIds,          // extension: allow util to expand for multiple products
        };

        let batchId = newBatch.id;
        let events = [];

        // Preferred: your shared util
        if (typeof buildBatchEvents === "function") {
          const res = buildBatchEvents(payload, { multiProductIds: selectedIds, batchId });
          batchId = res.batchId || batchId;
          events = Array.isArray(res.events) ? res.events : [];
        }

        // Fallback: simple linear stages if util returns nothing
        if (!events.length) {
          const stages = [
            { name: "Prepare Clay", durationDays: 1, tag: "forming" },
            { name: "Form / Mold", durationDays: 2, tag: "forming" },
            { name: "Drying", durationDays: 3, tag: "drying" },
            { name: "Bisque Firing", durationDays: 1, tag: "bisque" },
            { name: "Glazing", durationDays: 1, tag: "glazing" },
            { name: "Glaze Firing", durationDays: 1, tag: "glazeFiring" },
            { name: "Finishing", durationDays: 1, tag: "qcPack" },
          ];
          const start = new Date(newBatch.startDate);
          for (const pid of selectedIds) {
            const prod = productIndex.get(String(pid)) || { name: "Product" };
            let offset = 0;
            stages.forEach((st, idx) => {
              const d0 = new Date(start); d0.setDate(d0.getDate() + offset);
              const d1 = new Date(d0); d1.setDate(d1.getDate() + (st.durationDays - 1));
              events.push({
                id: `${batchId}-${pid}-${idx}`,
                title: `${prod.name || prod.title || "Item"} – ${st.name}`,
                start: new Date(d0.getFullYear(), d0.getMonth(), d0.getDate(), 9, 0, 0).toISOString(),
                end: new Date(d1.getFullYear(), d1.getMonth(), d1.getDate(), 17, 0, 0).toISOString(),
                status: "planned",
                progress: 0,
                qty: 1,
                category: "production",
                taskType: st.name,
                tags: st.tag ? [st.tag] : undefined,
                batch: newBatch.name,
                batchId,
                productId: pid,
                source: "batch",
                notes: "",
              });
              offset += st.durationDays;
            });
          }
        }

        if (events.length) {
          upsertEvents(events);
        }
        play("success"); buzz("success");
      } catch (e) {
        console.error("[CreateBatchModal] auto-generate failed:", e);
        play("error"); buzz("error");
      }
    } else {
      play("success"); buzz("light");
    }

    onClose?.(true);
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex justify-center items-center p-6 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-batch-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) { play("tap"); buzz("light"); onClose?.(false); }
      }}
    >
      <div className="bg-zinc-800 text-white p-6 rounded-xl shadow-2xl max-w-lg w-full space-y-4 border border-white/10">
        <h2 id="create-batch-title" className="text-xl font-bold">
          {batchToEdit ? "Edit Batch" : "Create New Batch"}
        </h2>
        <p className="text-sm opacity-70 -mt-2">
          Group similar products into a production batch to streamline planning, cost tracking, and automatic task creation.
        </p>

        {err && (
          <div className="text-sm p-3 rounded border border-red-500/40 bg-red-500/10 text-red-200">
            {err}
          </div>
        )}

        {/* Batch Name */}
        <label className="block font-medium">Batch Name</label>
        <p className="text-xs opacity-70 mb-1">
          Choose a name that identifies this batch (e.g. “B24-10 Cups – May Run”).
        </p>
        <input
          type="text"
          value={batchName}
          onChange={(e) => setBatchName(e.target.value)}
          className="w-full p-2 rounded text-black"
          placeholder="e.g. B24-10 – Espresso Cups"
          autoFocus
        />

        {/* Product Selection */}
        <label className="block font-medium mt-3">Select Products</label>
        <p className="text-xs opacity-70 mb-1">
          Tick the products included in this batch. Costs are automatically summed below.
        </p>
        <div className="max-h-40 overflow-y-auto bg-zinc-700 rounded p-2 space-y-1">
          {products.length === 0 && (
            <p className="text-sm opacity-70 text-center py-2">
              No products found — add products in your library first.
            </p>
          )}
          {products.map((prod) => (
            <label key={prod.id} className="block">
              <input
                type="checkbox"
                checked={selectedIds.includes(prod.id)}
                onChange={() => toggleProduct(prod.id)}
                className="mr-2"
              />
              {prod.name || prod.title || "Item"} — ${Number(prod.finalPrice || 0).toFixed(2)}
            </label>
          ))}
        </div>

        {/* Start Date */}
        <label className="block font-medium mt-3">Start Date</label>
        <p className="text-xs opacity-70 mb-1">When production for this batch is planned to begin.</p>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="w-full p-2 rounded text-black"
        />

        {/* Status — normalized to widget vocabulary */}
        <label className="block font-medium mt-3">Status</label>
        <p className="text-xs opacity-70 mb-1">Track whether this batch is upcoming, active, or completed.</p>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full p-2 rounded text-black"
        >
          <option>Not Started</option>
          <option>In Progress</option>
          <option>Completed</option>
          <option>Delayed</option>
        </select>

        {/* Auto-generate tasks */}
        {!batchToEdit && (
          <label className="block mt-3 text-sm">
            <input
              type="checkbox"
              checked={autoGenerateTasks}
              onChange={() => setAutoGenerateTasks(!autoGenerateTasks)}
              className="mr-2"
            />
            Auto-generate planner tasks
            <span className="block text-xs opacity-70 ml-5">
              Automatically create drying, bisque, glazing, and firing stages for each selected product.
            </span>
          </label>
        )}

        <p className="text-right text-sm mt-3">
          <span className="opacity-70">Total Cost:</span>{" "}
          <span className="font-semibold">${totalCost.toFixed(2)}</span>
        </p>

        <div className="flex justify-between items-center gap-2 pt-2">
          <div className="text-xs opacity-60">Esc to close • ⌘/Ctrl+Enter to save</div>
          <div className="flex gap-2">
            <button
              onClick={() => { play("tap"); buzz("light"); onClose?.(false); }}
              className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
